import { useEffect, useState, memo } from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { CheckCircle, History2 } from "@solar-icons/react/ssr";

import { request } from "@/Api";
import { colorPalette } from "@/themes";
import { formattedCurrency } from "@/utils";
import { WidgetHeaderRow, ListRowDivider, SeeAllRow } from "../primitives";

const colLabelSx = {
    fontSize: 11,
    color: "text.secondary",
    fontFamily: "Inter Tight, sans-serif",
    lineHeight: 1,
    flexShrink: 0,
};
const colTextSx = {
    fontSize: 12,
    color: "text.primary",
    fontFamily: "Inter Tight, sans-serif",
    lineHeight: 1,
};

const RecentBids = memo(() => {
    const theme = useTheme();
    const [bids, setBids] = useState(null);

    useEffect(() => {
        request
            .get("/dashboard/recent-bids", { params: { limit: 10 } })
            .then((r) => setBids(r.data))
            .catch(() => setBids([]));
    }, []);

    const isSigned = (bid) => bid.status?.toLowerCase() === "signed";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                height: "100%",
            }}
        >
            <WidgetHeaderRow
                title="Your Recent Bids"
                count={bids?.length ?? null}
            />

            {/* Scrollable table container */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Box sx={{ minWidth: 360 }}>
                    {/* Column headers */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            px: "8px",
                            gap: "20px",
                            flexShrink: 0,
                            mb: "4px",
                        }}
                    >
                        <Typography
                            sx={{ ...colLabelSx, flex: 1, minWidth: 140 }}
                        >
                            Name
                        </Typography>
                        <Typography
                            sx={{
                                ...colLabelSx,
                                flex: 1,
                                textAlign: "right",
                            }}
                        >
                            Quoted
                        </Typography>
                        <Typography
                            sx={{
                                ...colLabelSx,
                                width: 40,
                                textAlign: "center",
                            }}
                        >
                            Status
                        </Typography>
                    </Box>

                    {/* Rows */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                        }}
                    >
                        {!bids &&
                            [1, 2, 3, 4, 5].map((i) => (
                                <Skeleton
                                    key={i}
                                    variant="rounded"
                                    height={44}
                                    sx={{
                                        borderRadius: "6px",
                                        bgcolor: "action.hover",
                                    }}
                                />
                            ))}
                        {bids?.map((bid, i) => (
                            <Box
                                key={bid.id || i}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                }}
                            >
                                {i > 0 && <ListRowDivider />}
                                <Box
                                    component="a"
                                    href={`/bids/${bid.id}`}
                                    sx={{
                                        display: "flex",
                                        height: 54,
                                        alignItems: "center",
                                        gap: "20px",
                                        px: "8px",
                                        py: "12px",
                                        borderRadius: "6px",
                                        textDecoration: "none",
                                        "&:hover": {
                                            bgcolor: "action.hover",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 140,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "2px",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                ...colTextSx,
                                                fontSize: 14,
                                                overflow: "auto",
                                                whiteSpace: "nowrap",
                                                scrollbarWidth: "none",
                                                "&::-webkit-scrollbar": {
                                                    display: "none",
                                                },
                                            }}
                                        >
                                            {bid.name}
                                        </Typography>
                                        {bid.project?.name && (
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: "text.secondary",
                                                    fontFamily:
                                                        "Inter Tight, sans-serif",
                                                    lineHeight: 1.3,
                                                    overflow: "auto",
                                                    whiteSpace: "nowrap",
                                                    scrollbarWidth: "none",
                                                    "&::-webkit-scrollbar": {
                                                        display: "none",
                                                    },
                                                }}
                                            >
                                                {bid.project.name}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            ...colTextSx,
                                            flex: 1,
                                            textAlign: "right",
                                            fontSize: 14,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {formattedCurrency(
                                            bid.client_quoted,
                                            bid.project?.currency || "CAD",
                                        )}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 40,
                                            display: "flex",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isSigned(bid) ? (
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    background:
                                                        colorPalette.green.dark
                                                            .three,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <CheckCircle
                                                    size={12}
                                                    color={
                                                        colorPalette.green.dark
                                                            .main
                                                    }
                                                    weight="Bold"
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    background:
                                                        "secondary.main",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <History2
                                                    size={12}
                                                    color={
                                                        theme.palette.text
                                                            .secondary
                                                    }
                                                    weight="Bold"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                        {bids?.length === 0 && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "text.secondary",
                                    fontFamily: "Inter Tight, sans-serif",
                                    px: "8px",
                                }}
                            >
                                No bids yet
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            <SeeAllRow href="/bids" label="All bids" />
        </Box>
    );
});

RecentBids.displayName = "RecentBids";

export default RecentBids;
